import { BookOpen, ExternalLink } from "lucide-react";
import { WEBHOOK_EVENTS } from "@ambulance/shared";
import { apiUrl, oidcConfig } from "../auth";
import { useMe } from "../hooks/useMe";
import { Forbidden, Spinner } from "../components/ui";

const Code = ({ children }: { children: string }) => <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto">{children}</pre>;

/** Super admin: how external systems integrate (tokens, rate limits, webhooks, OpenAPI). */
export function DevelopersPage() {
  const { me, isLoading } = useMe();
  if (isLoading) return <Spinner />;
  if (!me || me.role !== "super_admin") return <Forbidden />;
  const tokenUrl = `${oidcConfig.authority}/protocol/openid-connect/token`;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Developers</h1>
        <p className="text-sm text-slate-600">The same API that powers these pages is open to your other systems, with their own identities and read-only access. Everything is documented in OpenAPI 3 and can be tried from the interactive page.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={`${apiUrl}/api/docs`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold px-4 py-2 text-sm"><BookOpen className="w-4 h-4" /> Interactive API docs (Swagger UI) <ExternalLink className="w-3.5 h-3.5" /></a>
          <a href={`${apiUrl}/api/docs-json`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2 text-sm">OpenAPI JSON <ExternalLink className="w-3.5 h-3.5" /></a>
        </div>
      </div>

      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-semibold text-slate-900">1. Authenticate (OAuth2 client credentials)</h2>
        <p className="text-sm text-slate-600">Create a client under <b>API Clients</b>, then exchange its credentials for a short-lived access token (15 minutes). Tokens are scoped to this tenant and are read-only.</p>
        <Code>{`curl -X POST ${tokenUrl} \\
  -d grant_type=client_credentials \\
  -d client_id=YOUR_CLIENT_ID \\
  -d client_secret=YOUR_CLIENT_SECRET
# -> { "access_token": "...", "expires_in": 900 }`}</Code>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-semibold text-slate-900">2. Call the API</h2>
        <p className="text-sm text-slate-600">Base URL <span className="font-mono">{apiUrl}/api/v1</span>. Lists are cursor-paginated: pass <span className="font-mono">?limit=</span> and the returned <span className="font-mono">nextCursor</span> as <span className="font-mono">?cursor=</span>.</p>
        <Code>{`curl "${apiUrl}/api/v1/checklists?limit=50" -H "Authorization: Bearer ACCESS_TOKEN"
curl "${apiUrl}/api/v1/checklists/{id}"          # full document
curl -X POST "${apiUrl}/api/v1/checklists/{id}/pdf" -H "Authorization: Bearer ACCESS_TOKEN" -o checklist.pdf
curl "${apiUrl}/api/v1/units/active"             # units on shift
curl "${apiUrl}/api/v1/bag-checklists?limit=100"
curl "${apiUrl}/api/v1/hospitals"  ·  /ambulances  ·  /unit-codes`}</Code>
        <p className="text-sm text-slate-600">Rate limit: 120 requests per minute per client by default (adjustable per client). Watch <span className="font-mono">X-RateLimit-Remaining</span>; a <span className="font-mono">429</span> carries <span className="font-mono">Retry-After</span>. Every call is recorded in the audit log.</p>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-semibold text-slate-900">3. Receive webhooks</h2>
        <p className="text-sm text-slate-600">Subscribe an https endpoint under <b>Webhooks</b>. Each event is POSTed as JSON with these headers: <span className="font-mono">X-Ambulance-Event</span>, <span className="font-mono">X-Ambulance-Delivery</span>, <span className="font-mono">X-Ambulance-Timestamp</span>, <span className="font-mono">X-Ambulance-Signature</span>. Reply 2xx within 10 seconds; anything else is retried with exponential backoff (8 attempts, about 4 hours). Payloads carry identifiers only; fetch details from the API.</p>
        <Code>{`{
  "id": "0192...",                    // delivery id (use it to de-duplicate)
  "event": "checklist.created",
  "createdAt": "2026-09-06T05:10:00.000Z",
  "data": { "id": "0192...", "hospitalId": "0192...", "occurredAt": "..." }
}`}</Code>
        <p className="text-sm text-slate-600">Verify the signature (Node.js example):</p>
        <Code>{`import { createHmac, timingSafeEqual } from "node:crypto";

function verify(secret, rawBody, header) {           // header = X-Ambulance-Signature
  const { t, v1 } = Object.fromEntries(header.split(",").map((p) => p.split("=")));
  if (Math.abs(Date.now() / 1000 - Number(t)) > 300) return false;   // 5-minute window
  const expected = createHmac("sha256", secret).update(\`\${t}.\${rawBody}\`).digest("hex");
  return timingSafeEqual(Buffer.from(expected), Buffer.from(v1));
}`}</Code>
        <div className="text-sm text-slate-600">Events: <span className="font-mono text-xs">{WEBHOOK_EVENTS.join(", ")}</span>, plus <span className="font-mono text-xs">ping</span> for tests.</div>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-2">
        <h2 className="font-semibold text-slate-900">4. Versioning and support</h2>
        <p className="text-sm text-slate-600">All routes live under <span className="font-mono">/api/v1</span>. Breaking changes ship under a new version while v1 stays available for an announced period. Response times, request ids and errors follow the shapes documented in Swagger.</p>
      </section>
    </div>
  );
}
